import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Crown } from 'lucide-react';
import LegalPageLayout, { LegalSection } from '../components/LegalPageLayout';
import { useLandingMetadata } from '../hooks/useLandingMetadata';

/**
 * Phase 40 · FAQ page.
 *
 * Two audiences share the same layout skeleton:
 *   - /faq/users   → FAQ per gli utenti/spettatori
 *   - /faq/creators → FAQ per i creator VIP
 *
 * A leading tab-bar lets the visitor jump between the two audiences
 * without losing the standalone/AppShell chrome from LegalPageLayout.
 * Contents remain empty until Nymia's ChatGPT-authored copy is added
 * (each section shows `meta.empty_section` as a hint).
 */

const USER_SECTION_IDS = [
  'getting_started',
  'account',
  'content_and_lives',
  'wallet_and_payments',
  'subscriptions_tickets',
  'refunds_disputes',
  'privacy_safety',
  'support',
];

const CREATOR_SECTION_IDS = [
  'becoming_creator',
  'stripe_onboarding',
  'creating_content',
  'hosting_lives',
  'one_to_one',
  'earnings_payouts',
  'moderation_rules',
  'taxes_fiscal',
  'support',
];

const LAST_UPDATED = '2026-02-12';

const FaqTabs = ({ audience }) => {
  const { t } = useTranslation('faq');
  return (
    <div className="mb-8 flex flex-wrap gap-2" data-testid="faq-audience-tabs">
      <Link
        to="/faq/users"
        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
          audience === 'users'
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-100'
            : 'border-border bg-card text-muted-foreground hover:text-amber-200'
        }`}
        data-testid="faq-tab-users"
      >
        <Users className="h-3.5 w-3.5" />
        {t('tabs.users')}
      </Link>
      <Link
        to="/faq/creators"
        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
          audience === 'creators'
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-100'
            : 'border-border bg-card text-muted-foreground hover:text-amber-200'
        }`}
        data-testid="faq-tab-creators"
      >
        <Crown className="h-3.5 w-3.5" />
        {t('tabs.creators')}
      </Link>
    </div>
  );
};

const FaqPage = ({ audience: propAudience }) => {
  const { t } = useTranslation('faq');
  const location = useLocation();
  // Derive audience from route path if not passed explicitly.
  const audience = propAudience || (location.pathname.includes('creators') ? 'creators' : 'users');

  const isUsers = audience === 'users';
  useLandingMetadata(isUsers ? 'faq_users' : 'faq_creators');
  const sectionIds = isUsers ? USER_SECTION_IDS : CREATOR_SECTION_IDS;
  const rootKey = isUsers ? 'users' : 'creators';

  const sections = sectionIds.map((id) => ({
    id,
    title: t(`${rootKey}.sections.${id}`),
  }));

  return (
    <LegalPageLayout
      namespace="faq"
      eyebrow={isUsers ? 'Nymia · Help center' : 'Nymia · Voice Pro hub'}
      title={t(`${rootKey}.title`)}
      subtitle={t(`${rootKey}.subtitle`)}
      lastUpdated={LAST_UPDATED}
      sections={sections}
      testId={`faq-${audience}-page`}
    >
      <FaqTabs audience={audience} />
      {sectionIds.map((id) => (
        <LegalSection
          key={id}
          id={id}
          title={t(`${rootKey}.sections.${id}`)}
          body={t(`${rootKey}.body.${id}`, { defaultValue: '' })}
          testId={`faq-${audience}-section-${id}`}
        />
      ))}
    </LegalPageLayout>
  );
};

export default FaqPage;
